import { createContext, type ReactNode, useContext, useMemo } from "react";
import { PRODUCT_VIEWER_ASSET_MANIFEST } from "./productViewerAssetManifest.mjs";
import { trimTrailingSlashes } from "./trimTrailingSlashes.mjs";

export interface ProductViewerAssets {
  readonly environment: string;
  readonly floor: {
    readonly map: string;
    readonly normalMap: string;
    readonly arm: string;
  };
}

/**
 * Append a manifest path to a directory URL, without dropping its subpath.
 * An omitted/empty base preserves the historical origin-root URLs. Relative
 * bases remain document-relative; absolute HTTP(S) and protocol-relative CDN
 * bases work without consulting window/location (including during SSR).
 */
export function resolveProductViewerAssetUrl(
  assetPath: string,
  stageAssetBaseUrl = "",
): string {
  const base = trimTrailingSlashes(stageAssetBaseUrl.trim());
  if (
    /[?#\\]/.test(base) ||
    (/^[a-z][a-z\d+.-]*:/i.test(base) && !/^https?:\/\//i.test(base))
  ) {
    throw new Error(
      "stageAssetBaseUrl must be an HTTP(S) URL or path to a directory, " +
        "without a query string or fragment",
    );
  }
  const path = assetPath.replace(/^\/+/, "");
  if (
    !path ||
    /[?#\\:]/.test(path) ||
    path.split("/").some((segment) => segment === "." || segment === "..")
  ) {
    throw new Error("Expected a relative product viewer asset file path");
  }
  return `${base}/${path}`;
}

export function resolveProductViewerAssets(
  stageAssetBaseUrl?: string,
): ProductViewerAssets {
  const url = (path: string) =>
    resolveProductViewerAssetUrl(path, stageAssetBaseUrl);
  return Object.freeze({
    environment: url(PRODUCT_VIEWER_ASSET_MANIFEST.environment.path),
    floor: Object.freeze({
      map: url(PRODUCT_VIEWER_ASSET_MANIFEST.floorMap.path),
      normalMap: url(PRODUCT_VIEWER_ASSET_MANIFEST.floorNormalMap.path),
      arm: url(PRODUCT_VIEWER_ASSET_MANIFEST.floorArmMap.path),
    }),
  });
}

export const DEFAULT_PRODUCT_VIEWER_ASSETS = resolveProductViewerAssets();

const ProductViewerAssetsContext = createContext<ProductViewerAssets>(
  DEFAULT_PRODUCT_VIEWER_ASSETS,
);

export interface ProductViewerAssetsProviderProps {
  readonly children: ReactNode;
  /** Directory containing the manifest's hdri/ and textures/floor/ trees. */
  readonly stageAssetBaseUrl?: string;
}

/** Independent of model URL configuration; no process/global mutable base. */
export function ProductViewerAssetsProvider({
  children,
  stageAssetBaseUrl,
}: ProductViewerAssetsProviderProps) {
  const assets = useMemo(
    () => resolveProductViewerAssets(stageAssetBaseUrl),
    [stageAssetBaseUrl],
  );
  return (
    <ProductViewerAssetsContext.Provider value={assets}>
      {children}
    </ProductViewerAssetsContext.Provider>
  );
}

export function useProductViewerAssets(): ProductViewerAssets {
  return useContext(ProductViewerAssetsContext);
}
